import { z } from "zod";
import { isSupportedTimeZone } from "./calendar-time.js";
import { taskRunCapabilitySchema } from "./primitives.js";
import { memoryRunPolicySchema, UNATTENDED_MEMORY_RUN_POLICY, } from "./memory-policy.js";
const idSchema = z.string().trim().min(1).max(256);
const dailyTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u);
export const SCHEDULED_SESSION_PROMPT_MAX_UTF16_UNITS = 20_000;
const SCHEDULED_SESSION_PROMPT_MAX_LENGTH_MESSAGE = "Automation prompt must be 20,000 UTF-16 code units or fewer.";
const scheduledSessionPromptSchema = z
    .string()
    .trim()
    .min(1)
    .max(SCHEDULED_SESSION_PROMPT_MAX_UTF16_UNITS, {
    message: SCHEDULED_SESSION_PROMPT_MAX_LENGTH_MESSAGE,
});
export function scheduledSessionPromptUsage(prompt) {
    const used = prompt.length;
    return {
        unit: "utf16_code_units",
        used,
        limit: SCHEDULED_SESSION_PROMPT_MAX_UTF16_UNITS,
        remaining: Math.max(0, SCHEDULED_SESSION_PROMPT_MAX_UTF16_UNITS - used),
    };
}
const timeZoneSchema = z
    .string()
    .trim()
    .min(1)
    .max(100)
    .refine(isSupportedTimeZone, "timeZone must be a valid IANA timezone");
export const scheduledSessionDaySchema = z.enum([
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
]);
/*
 * Optional weekday mask. Omitted (or null) means every day — the pre-existing
 * behaviour. A typed enum rather than cron: agents author schedules through
 * the CLI and in-session tools, and named days fail loudly on typos where a
 * cron expression would silently schedule the wrong day.
 */
export const scheduledSessionDaysOfWeekSchema = z
    .array(scheduledSessionDaySchema)
    .min(1)
    .max(7)
    .refine((days) => new Set(days).size === days.length, {
    message: "daysOfWeek must not repeat days.",
});
export const scheduledSessionDaysOfMonthSchema = z
    .array(z.number().int().min(1).max(31))
    .min(1)
    .max(31)
    .refine((days) => new Set(days).size === days.length, {
    message: "daysOfMonth must not repeat days.",
});
export const scheduledSessionNthWeekdaySchema = z.object({
    ordinal: z.union([
        z.literal(-1),
        z.number().int().min(1).max(5),
    ]),
    day: scheduledSessionDaySchema,
}).strict();
export const scheduledSessionNthWeekdaysSchema = z
    .array(scheduledSessionNthWeekdaySchema)
    .min(1)
    .max(42)
    .refine((values) => new Set(values.map((value) => `${value.ordinal}:${value.day}`)).size ===
    values.length, { message: "nthWeekdays must not repeat rules." });
function hasOneCalendarRecurrence(value) {
    return [value.daysOfWeek, value.daysOfMonth, value.nthWeekdays]
        .filter((entry) => entry !== undefined && entry !== null).length <= 1;
}
const CALENDAR_RECURRENCE_MESSAGE = "Choose only one of daysOfWeek, daysOfMonth, or nthWeekdays.";
export const scheduledSessionRequiredToolSchema = z.object({
    toolName: z.string().trim().min(1).max(200),
    minimumCalls: z.number().int().min(1).max(100).default(1),
});
export const scheduledSessionSuccessCriteriaSchema = z.object({
    failOnAnyToolError: z.boolean().default(true),
    requiredSuccessfulTools: z
        .array(scheduledSessionRequiredToolSchema)
        .max(50)
        .default([]),
});
const scheduledSessionRequestFields = z.object({
    title: z.string().trim().min(1).max(200),
    prompt: scheduledSessionPromptSchema,
    dailyAtUtc: dailyTimeSchema,
    dailyLocalTime: dailyTimeSchema.optional(),
    timeZone: timeZoneSchema.optional(),
    requestedCapabilities: z
        .array(taskRunCapabilitySchema)
        .max(5)
        .default(["readonly-data", "writeback"]),
    successCriteria: scheduledSessionSuccessCriteriaSchema.default({
        failOnAnyToolError: true,
        requiredSuccessfulTools: [],
    }),
    memoryPolicy: memoryRunPolicySchema.default(UNATTENDED_MEMORY_RUN_POLICY),
    connectionIds: z.array(idSchema).max(50).default([]),
    targetChannelIds: z.array(idSchema).max(50).default([]),
    enabled: z.boolean().default(true),
    daysOfWeek: scheduledSessionDaysOfWeekSchema.optional(),
    daysOfMonth: scheduledSessionDaysOfMonthSchema.optional(),
    nthWeekdays: scheduledSessionNthWeekdaysSchema.optional(),
});
export const createScheduledSessionRequestSchema = scheduledSessionRequestFields
    .refine((value) => Boolean(value.dailyLocalTime) === Boolean(value.timeZone), {
    message: "dailyLocalTime and timeZone must be provided together.",
})
    .refine(hasOneCalendarRecurrence, {
    message: CALENDAR_RECURRENCE_MESSAGE,
});
export const patchScheduledSessionRequestSchema = z.object({
    title: z.string().trim().min(1).max(200).optional(),
    prompt: scheduledSessionPromptSchema.optional(),
    dailyAtUtc: dailyTimeSchema.optional(),
    dailyLocalTime: dailyTimeSchema.optional(),
    timeZone: timeZoneSchema.optional(),
    requestedCapabilities: z.array(taskRunCapabilitySchema).max(5).optional(),
    successCriteria: scheduledSessionSuccessCriteriaSchema.optional(),
    memoryPolicy: memoryRunPolicySchema.optional(),
    connectionIds: z.array(idSchema).max(50).optional(),
    targetChannelIds: z.array(idSchema).max(50).optional(),
    enabled: z.boolean().optional(),
    // null clears the mask back to every-day; undefined leaves it unchanged.
    daysOfWeek: scheduledSessionDaysOfWeekSchema.nullable().optional(),
    daysOfMonth: scheduledSessionDaysOfMonthSchema.nullable().optional(),
    nthWeekdays: scheduledSessionNthWeekdaysSchema.nullable().optional(),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: "At least one field is required.",
})
    .refine((value) => (value.dailyLocalTime === undefined && value.timeZone === undefined) ||
    (value.dailyLocalTime !== undefined && value.timeZone !== undefined), { message: "dailyLocalTime and timeZone must be provided together." })
    .refine(hasOneCalendarRecurrence, {
    message: CALENDAR_RECURRENCE_MESSAGE,
});
/**
 * Why the system, not a person, paused an automation: its owner may no longer
 * run it as it is (twin-tenant scheduled_session_access.ts).
 */
export const scheduledSessionPauseReasonSchema = z.enum([
    "destination_unavailable",
    "owner_offboarded",
    "organisation_missing",
]);
/** What a person reads about a system pause, wherever it is shown. */
export const SCHEDULED_SESSION_PAUSE_REASON_COPY = {
    destination_unavailable: "Paused because a destination channel was archived or you no longer have access to it.",
    owner_offboarded: "Paused because its owner is no longer a member of the organisation.",
    organisation_missing: "Paused because it no longer belongs to an organisation.",
};
export const scheduledSessionSchema = z.object({
    id: idSchema,
    tenantSlug: z.string().trim().min(1),
    organisationId: idSchema.nullable(),
    userId: idSchema,
    canManage: z.boolean(),
    title: z.string(),
    prompt: z.string(),
    requestedCapabilities: z.array(taskRunCapabilitySchema),
    successCriteria: scheduledSessionSuccessCriteriaSchema,
    memoryPolicy: memoryRunPolicySchema,
    connectionIds: z.array(idSchema),
    targetChannelIds: z.array(idSchema),
    dailyAtUtc: dailyTimeSchema,
    dailyLocalTime: dailyTimeSchema.nullable(),
    timeZone: z.string().nullable(),
    daysOfWeek: scheduledSessionDaysOfWeekSchema.nullable(),
    daysOfMonth: scheduledSessionDaysOfMonthSchema.nullable(),
    nthWeekdays: scheduledSessionNthWeekdaysSchema.nullable(),
    enabled: z.boolean(),
    /**
     * Set while the system keeps the automation paused; null when it runs or a
     * person paused it. Optional only while the public gateway still reads
     * tenants that predate it: make it required once every tenant serves it.
     */
    pauseReason: scheduledSessionPauseReasonSchema.nullable().optional(),
    lastRunAt: z.number().int().nullable(),
    lastRunStatus: z.string().nullable(),
    nextRunAt: z.number().int(),
    createdAt: z.number().int(),
    updatedAt: z.number().int(),
});
export const listScheduledSessionsResponseSchema = z.object({
    scheduledSessions: z.array(scheduledSessionSchema),
});
export const scheduledSessionResponseSchema = z.object({
    scheduledSession: scheduledSessionSchema,
});
export const scheduledSessionRunSchema = z.object({
    id: idSchema,
    status: z.string(),
    createdAt: z.number().int(),
    finishedAt: z.number().int().nullable(),
    diagnosticId: z.string().nullable(),
    failureCode: z.enum([
        "routing_failed",
        "tool_failed",
        "required_tool_missing",
    ]).nullable(),
    failedTools: z.array(z.string()),
    successfulTools: z.array(z.string()),
    missingRequiredTools: z.array(z.string()),
});
export const listScheduledSessionRunsResponseSchema = z.object({
    runs: z.array(scheduledSessionRunSchema),
    totalRuns: z.number().int().nonnegative(),
    totalRunsCapped: z.boolean(),
    successRate: z.number().min(0).max(1).nullable(),
});
export const automationPathParamsSchema = z.object({ automationId: idSchema.optional() }).strict();
